/**
 * COMPOSER UNE SÉQUENCE — chantier « manuel vivant », Φ-MV-5 (MV-TUT-06, F-11,
 * F-14). `/eleves/:id/manuel/composer[?sequence=<id>]`.
 *
 * Depuis P3 (AB#975), cette page ne DÉPOSE plus : sans `?sequence=`, elle
 * renvoie au compositeur de parcours (`ComposerParcoursPage`), ouvert sur
 * l'élève. Elle garde la retouche d'une séquence déjà déposée, à plat.
 *
 * Le tuteur choisit dans la banque du chapitre des exercices, des fiches et des
 * capsules, les ordonne, y joint un mot et une échéance, et DÉPOSE la séquence
 * dans la liste de l'élève — qui la retrouve en tête de sa page de travail,
 * son premier élément non fait devenant son prochain geste. Avec `?sequence=`,
 * la même page MODIFIE une séquence en cours : la liste envoyée remplace
 * l'ancienne dans l'ordre donné, et un élément déjà fait garde sa date.
 *
 * ─── Deux colonnes, deux choses ouvertes (MV-UX-02) ─────────────────────────
 *
 * À gauche la banque, à droite la liste. Les filtres de la banque sont repliés,
 * la raison de chaque élément aussi (elle est en second plan : sans elle, le
 * serveur écrit « Ajouté par le tuteur »). Un seul bouton plein : déposer.
 *
 * ─── Sous 1 000 px (revue UX `UX-7-15`) ─────────────────────────────────────
 *
 * Les deux colonnes s'empilent : la liste se retrouvait 1 100 px sous la
 * banque, et « Ajouter » ne montrait rien. Un BANDEAU COLLANT, en bas, dit
 * alors ce que la liste contient et ce qu'on vient d'y mettre, et y mène
 * (« Voir la liste »). Il ne dépose pas : une séquence veut un titre, qui se
 * saisit dans la liste — le seul bouton plein reste là. Au-dessus de 1 000 px,
 * la liste est à côté de la banque et le bandeau n'existe pas.
 *
 * La banque s'ouvre sur LA NOTION QUE LA CLASSE VOIT (elle s'ouvrait sur la
 * première du chapitre) ; le résumé du rabat « Filtres » la nomme, et « Toutes
 * les notions » reste à un geste.
 *
 * ─── Ce que la banque montre ────────────────────────────────────────────────
 *
 * Ce qui est EN SERVICE (publié) : rien d'autre n'est déposable. Tous les
 * niveaux y sont (F-14) ; si le tuteur a limité le niveau de l'élève, un
 * exercice au-delà le dit sur sa ligne — le serveur le refuserait au dépôt.
 * Aucun code interne à l'écran : un exercice se dit par son énoncé.
 *
 * ─── Le refus ───────────────────────────────────────────────────────────────
 *
 * Le serveur refuse AU DÉPÔT, jamais en silence (422 `SEQUENCE_INVALIDE` : un
 * titre vide, un élément en double, une ressource sortie du service, un
 * exercice au-delà du niveau maximal). Sa raison, en une phrase, est affichée
 * telle quelle ; rien n'est perdu de ce qui était composé.
 *
 * ─── Ce qui manque à l'exigence ─────────────────────────────────────────────
 *
 * « D'après ses erreurs, voici ce que je mettrais » : le serveur de la phase 5
 * n'a pas de route de proposition. Le sous-titre rappelle l'erreur la plus
 * fréquente et l'évaluation qui approche, pour composer en connaissance de
 * cause ; « Reprendre la proposition » n'est pas à l'écran.
 *
 * ─── Préremplie depuis la préparation de séance (MV-TUT-04) ─────────────────
 *
 * « Déposer comme séquence », sur la page de préparation, ouvre cette page avec
 * un BROUILLON dans l'état de navigation : l'appui que le serveur a désigné
 * pour chaque priorité, dans l'ordre, avec sa raison. Rien n'est déposé de
 * là-bas — le tuteur relit ici, retire, complète, puis dépose. Un état absent
 * ou mal formé : la page s'ouvre vide, comme avant (`lireBrouillonTransmis`).
 */
import {
  LIBELLES_CATEGORIE_EXERCICE,
  MAX_CARACTERES_MOT_SEQUENCE,
  MAX_CARACTERES_RAISON_ELEMENT,
  MAX_CARACTERES_TITRE_SEQUENCE,
  MAX_ELEMENTS_SEQUENCE,
  denombrer,
  dureeEnClair,
  type GenreDeGeste,
  type NotionDeRessourceDto,
  type SequenceTuteurDto,
  type VueEleveTuteurResponse,
} from '@silex/shared';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { api } from '../api/client';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { SectionRabattable } from '../components/SectionRabattable';
import { TexteMaths } from '../components/TexteMaths';
import { manuelVivantActif } from '../lib/manuel-vivant';
import {
  adressesManuelEleve,
  adressesParcours,
  cleDElement,
  corpsDeposerSequence,
  corpsModifierSequence,
  dansCombien,
  deplacer,
  elementsDeSequence,
  nomDeNiveau,
  nomDeNotion,
  type BrouillonSequence,
  type ElementCompose,
} from '../lib/manuel-tuteur-format';
import { lireBrouillonTransmis } from '../lib/manuel-tuteur-lectures';
import { messageErreur, useAsync } from '../lib/useAsync';

/** Une ligne de la banque : de quoi la lire, la filtrer et l'ajouter. */
interface LigneDeBanque {
  readonly genre: GenreDeGeste;
  readonly code: string;
  readonly titre: string;
  /** « Exercice · 2 · Intermédiaire », « Fiche », « Capsule » — repris dans la liste. */
  readonly sorte: string;
  /** La ligne grise : catégorie, notions, durée. */
  readonly sous: string;
  readonly notions: readonly NotionDeRessourceDto[];
  readonly niveau: 1 | 2 | 3 | 4 | null;
}

const GENRES: readonly { readonly genre: GenreDeGeste; readonly libelle: string }[] = [
  { genre: 'exercice', libelle: 'Exercices' },
  { genre: 'fiche', libelle: 'Fiches' },
  { genre: 'capsule', libelle: 'Capsules' },
];

const VIDE: BrouillonSequence = { titre: '', mot: '', pourLe: '', elements: [] };

export function ComposerSequencePage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const sequenceId = params.get('sequence');
  const actif = manuelVivantActif();
  const location = useLocation();

  const vue = useAsync(
    useMemo(
      () => (actif && id && sequenceId !== null ? () => api.manuelVueEleve(id) : null),
      [actif, id, sequenceId],
    ),
  );
  /* La séquence à modifier se lit dans la liste de l'élève : pas de route à l'unité. */
  const aModifier = useAsync(
    useMemo(
      () =>
        actif && id && sequenceId
          ? () =>
              api
                .manuelSequences(id)
                .then((r) => r.sequences.find((s) => s.id === sequenceId) ?? null)
          : null,
      [actif, id, sequenceId],
    ),
  );

  if (!actif) return <Navigate to="/" replace />;
  if (!id) return <Navigate to="/eleves" replace />;
  /* P3 (AB#975) : DÉPOSER se fait désormais par le compositeur de parcours,
     ouvert sur cet élève ; le brouillon de la préparation de séance suit. La
     retouche d'une séquence déposée (`?sequence=`) reste ici. */
  if (sequenceId === null) {
    return (
      <Navigate to={adressesParcours.composer({ eleve: id })} state={location.state} replace />
    );
  }

  const retour = adressesManuelEleve.fiche(id, 'sequences');
  const attente = vue.status === 'loading' || aModifier.status === 'loading';

  return (
    <div className="content__inner manuel-tuteur">
      <p className="note-line fil-retour">
        <Link to={retour}>← Ses séquences</Link>
      </p>
      {attente ? <Chargement quoi="la banque du chapitre" /> : null}
      {vue.status === 'error' ? <ErreurRessource message={vue.message} /> : null}
      {aModifier.status === 'error' ? <ErreurRessource message={aModifier.message} /> : null}
      {vue.status === 'ready' && aModifier.status === 'ready' ? (
        aModifier.data === null || !aModifier.data.laMienne || aModifier.data.etat === 'retiree' ? (
          <div className="callout callout--rare" role="alert">
            <strong>Cette séquence ne se modifie pas.</strong> Elle a été retirée, ou déposée par un
            autre tuteur.
          </div>
        ) : (
          <Composition vue={vue.data} sequence={aModifier.data} retour={retour} />
        )
      ) : null}
    </div>
  );
}

/** La composition, une fois la vue lue : son état d'arrivée s'éprouve par son rendu. */
export function Composition({
  vue,
  sequence,
  retour,
}: {
  readonly vue: VueEleveTuteurResponse;
  readonly sequence: SequenceTuteurDto | null;
  readonly retour: string;
}) {
  const id = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const { eleve, chapitre } = vue;
  const [b, setB] = useState<BrouillonSequence>(() =>
    sequence === null
      ? (lireBrouillonTransmis(location.state) ?? VIDE)
      : {
          titre: sequence.titre,
          mot: sequence.mot ?? '',
          pourLe: sequence.pourLe ?? '',
          elements: elementsDeSequence(sequence),
        },
  );
  const [genre, setGenre] = useState<GenreDeGeste>('exercice');
  /* La notion du moment, si elle est bien une notion du chapitre ; sinon tout le chapitre. */
  const [notion, setNotion] = useState(() => {
    const duMoment = vue.classe?.notionDuMoment?.code;
    return vue.notions.some((n) => n.notion.code === duMoment) ? (duMoment ?? '') : '';
  });
  const liste = useRef<HTMLElement>(null);
  const [niveau, setNiveau] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const code = chapitre.code;
  const exercices = useAsync(useMemo(() => () => api.manuelExercicesChapitre(code), [code]));
  const savoir = useAsync(useMemo(() => () => api.savoirChapitre(code), [code]));

  /* Le filtre de niveau ne vaut que pour les exercices : on l'oublie ailleurs. */
  useEffect(() => {
    if (genre !== 'exercice') setNiveau('');
  }, [genre]);

  const banque: readonly LigneDeBanque[] = useMemo(() => {
    const nommer = (notions: readonly NotionDeRessourceDto[]): string =>
      notions.map((n) => nomDeNotion(n, chapitre.rang)).join(' · ');
    if (genre === 'exercice') {
      if (exercices.status !== 'ready') return [];
      return exercices.data.exercices
        .filter((e) => e.statut === 'publie')
        .map((e) => ({
          genre: 'exercice' as const,
          code: e.code,
          titre: e.titre ?? e.enonce,
          sorte: `Exercice · ${nomDeNiveau(e.niveauExercice)}`,
          sous: `${nomDeNiveau(e.niveauExercice)} · ${LIBELLES_CATEGORIE_EXERCICE[e.categorie]} · ${nommer([e.notion])}`,
          notions: [e.notion],
          niveau: e.niveauExercice,
        }));
    }
    if (savoir.status !== 'ready') return [];
    if (genre === 'fiche') {
      return savoir.data.fiches
        .filter((f) => f.relecture.statut === 'publie')
        .map((f) => ({
          genre: 'fiche' as const,
          code: f.code,
          titre: f.titre,
          sorte: 'Fiche',
          sous: `${f.minutesLecture} min de lecture · ${nommer(f.notions)}`,
          notions: f.notions,
          niveau: null,
        }));
    }
    return (
      savoir.data.capsules
        /* T40 : une capsule à venir ne se regarde pas — elle ne s'assigne pas. */
        .filter((c) => c.relecture.statut === 'publie' && c.etat !== 'a_venir')
        .map((c) => ({
          genre: 'capsule' as const,
          code: c.code,
          titre: c.titre,
          sorte: 'Capsule',
          sous: `${dureeEnClair(c.dureeSec)} · ${nommer(c.notions)}`,
          notions: c.notions,
          niveau: null,
        }))
    );
  }, [genre, exercices, savoir, chapitre.rang]);

  const visibles = banque.filter(
    (l) =>
      (notion === '' || l.notions.some((n) => n.code === notion)) &&
      (niveau === '' || String(l.niveau) === niveau),
  );
  const dansLaListe = new Set(b.elements.map(cleDElement));
  const source = genre === 'exercice' ? exercices : savoir;
  const limite = vue.niveauMaximal?.niveauMaximal ?? null;
  const pleine = b.elements.length >= MAX_ELEMENTS_SEQUENCE;

  const changer = (morceau: Partial<BrouillonSequence>): void =>
    setB((avant) => ({ ...avant, ...morceau }));
  const ajouter = (l: LigneDeBanque): void =>
    changer({
      elements: [
        ...b.elements,
        { genre: l.genre, code: l.code, titre: l.titre, sorte: l.sorte, raison: '' },
      ],
    });
  const changerRaison = (e: ElementCompose, raison: string): void =>
    changer({
      elements: b.elements.map((x) => (cleDElement(x) === cleDElement(e) ? { ...x, raison } : x)),
    });

  const deposer = async (): Promise<void> => {
    setEnvoi(true);
    setRefus(null);
    try {
      if (sequence === null) {
        await api.manuelDeposerSequence(eleve.id, corpsDeposerSequence(b));
      } else {
        await api.manuelModifierSequence(sequence.id, corpsModifierSequence(b));
      }
      navigate(retour);
    } catch (err) {
      setRefus(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  /* Le résumé des filtres NOMME ce qui est filtré : la banque s'ouvre sur une
     notion, le tuteur doit le lire sans déplier. */
  const notionFiltree = vue.notions.find((n) => n.notion.code === notion)?.notion;
  const resumeFiltres =
    [
      notionFiltree === undefined ? null : nomDeNotion(notionFiltree, chapitre.rang),
      niveau === '' ? null : `niveau ${nomDeNiveau(Number(niveau) as 1 | 2 | 3 | 4)}`,
    ]
      .filter((m): m is string => m !== null)
      .join(' · ') || 'Tout le chapitre';
  const dernier = b.elements[b.elements.length - 1];

  const erreur = vue.erreursRecurrentes[0];
  const evaluation = vue.preparations[0]?.evaluation;
  const contexte = [
    evaluation === undefined
      ? null
      : `${evaluation.titre}, ${dansCombien(evaluation.joursRestants)}`,
    erreur === undefined
      ? null
      : `erreur la plus fréquente : ${erreur.titre} (${denombrer(erreur.fois, 'fois', 'fois')})`,
  ].filter((m): m is string => m !== null);

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">{chapitre.titre}</p>
          <h1 data-testid="composer-titre">
            {sequence === null
              ? `Déposer une séquence pour ${eleve.prenom}`
              : `Modifier la séquence de ${eleve.prenom}`}
          </h1>
          {contexte.length > 0 ? <p className="page-sub">{contexte.join(' · ')}</p> : null}
        </div>
      </div>

      <div className="tuteur-grid tuteur-grid--egal">
        {/* ── La banque du chapitre ─────────────────────────────────────── */}
        <section className="card" data-testid="composer-banque">
          <h3>La banque du chapitre</h3>
          <div className="segmented" role="group" aria-label="Type de ressource">
            {GENRES.map((g) => (
              <button
                key={g.genre}
                type="button"
                className={g.genre === genre ? 'segmented__btn is-active' : 'segmented__btn'}
                aria-pressed={g.genre === genre}
                onClick={() => setGenre(g.genre)}
                data-testid={`composer-genre-${g.genre}`}
              >
                {g.libelle}
              </button>
            ))}
          </div>

          <SectionRabattable
            variante="tuile"
            titre="Filtres"
            resume={resumeFiltres}
            ouverteParDefaut={false}
          >
            <div className="form-grid form-grid--serre">
              <div className="field field--inline">
                <label htmlFor={`${id}-notion`}>Notion</label>
                <select
                  id={`${id}-notion`}
                  value={notion}
                  onChange={(e) => setNotion(e.target.value)}
                >
                  <option value="">Toutes les notions</option>
                  {vue.notions.map((n) => (
                    <option key={n.notion.code} value={n.notion.code}>
                      {nomDeNotion(n.notion, chapitre.rang)}
                    </option>
                  ))}
                </select>
              </div>
              {genre === 'exercice' ? (
                <div className="field field--inline">
                  <label htmlFor={`${id}-niveau`}>Niveau</label>
                  <select
                    id={`${id}-niveau`}
                    value={niveau}
                    onChange={(e) => setNiveau(e.target.value)}
                  >
                    <option value="">Tous les niveaux</option>
                    {([1, 2, 3, 4] as const).map((n) => (
                      <option key={n} value={n}>
                        {nomDeNiveau(n)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          </SectionRabattable>

          {source.status === 'loading' ? <Chargement quoi="la banque" /> : null}
          {source.status === 'error' ? <ErreurRessource message={source.message} /> : null}
          {source.status === 'ready' && visibles.length === 0 ? (
            <p className="note-line">Rien ne correspond à ces filtres.</p>
          ) : null}
          {visibles.length > 0 ? (
            <div className="list banque-liste">
              {visibles.map((l) => {
                const deja = dansLaListe.has(cleDElement(l));
                const auDela = limite !== null && l.niveau !== null && l.niveau > limite;
                return (
                  <div key={cleDElement(l)} className="li li--haut" data-testid="composer-ligne">
                    <span className="li-main">
                      <span className="li-sub">{l.sous}</span>
                      <span className="banque-liste__titre">
                        <TexteMaths texte={l.titre} />
                      </span>
                      {auDela ? (
                        <span className="li-sub">
                          Au-delà du niveau que vous avez réglé pour {eleve.prenom}.
                        </span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={deja || pleine}
                      onClick={() => ajouter(l)}
                      data-testid="composer-ajouter"
                    >
                      {deja ? 'Dans la séquence' : 'Ajouter'}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
        </section>

        {/* ── La liste de l'élève ──────────────────────────────────────── */}
        <section className="card" data-testid="composer-liste" ref={liste}>
          <h3>Dans la liste de {eleve.prenom}</h3>
          <div className="field">
            <label htmlFor={`${id}-titre`}>Titre</label>
            <input
              id={`${id}-titre`}
              type="text"
              maxLength={MAX_CARACTERES_TITRE_SEQUENCE}
              value={b.titre}
              onChange={(e) => changer({ titre: e.target.value })}
              data-testid="composer-champ-titre"
            />
          </div>

          {b.elements.length === 0 ? (
            <p className="note-line">Séquence vide. Ajoutez depuis la banque.</p>
          ) : (
            <ol className="sequence-elements">
              {b.elements.map((e, i) => (
                <li
                  key={cleDElement(e)}
                  className="sequence-element"
                  data-testid="composer-element"
                >
                  <span className="sequence-element__rang" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="sequence-element__corps">
                    <span className="cell-sub">{e.sorte}</span>
                    <TexteMaths texte={e.titre} />
                    <details className="sequence-raison">
                      <summary>{e.raison.trim() === '' ? 'Dire pourquoi' : 'Pourquoi'}</summary>
                      <input
                        type="text"
                        aria-label={`Pourquoi l’élément ${i + 1} est dans la séquence`}
                        maxLength={MAX_CARACTERES_RAISON_ELEMENT}
                        placeholder="Ajouté par le tuteur"
                        value={e.raison}
                        onChange={(ev) => changerRaison(e, ev.target.value)}
                      />
                    </details>
                  </span>
                  <span className="sequence-element__gestes">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={i === 0}
                      aria-label={`Monter l’élément ${i + 1}`}
                      onClick={() => changer({ elements: deplacer(b.elements, i, -1) })}
                      data-testid="composer-monter"
                    >
                      Monter
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={i === b.elements.length - 1}
                      aria-label={`Descendre l’élément ${i + 1}`}
                      onClick={() => changer({ elements: deplacer(b.elements, i, 1) })}
                      data-testid="composer-descendre"
                    >
                      Descendre
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      aria-label={`Retirer l’élément ${i + 1}`}
                      onClick={() =>
                        changer({
                          elements: b.elements.filter((x) => cleDElement(x) !== cleDElement(e)),
                        })
                      }
                      data-testid="composer-retirer"
                    >
                      Retirer
                    </button>
                  </span>
                </li>
              ))}
            </ol>
          )}
          <p className="card-sub" data-testid="composer-compte">
            {denombrer(b.elements.length, 'élément')}
            {pleine ? ` · le plus qu’une séquence accepte` : ''}
          </p>

          <div className="field">
            <label htmlFor={`${id}-mot`}>Mot à {eleve.prenom}</label>
            <textarea
              id={`${id}-mot`}
              rows={3}
              maxLength={MAX_CARACTERES_MOT_SEQUENCE}
              placeholder="Par quoi commencer, à quoi faire attention"
              value={b.mot}
              onChange={(e) => changer({ mot: e.target.value })}
              data-testid="composer-champ-mot"
            />
          </div>
          <div className="field">
            <label htmlFor={`${id}-pour-le`}>À faire pour le</label>
            <input
              id={`${id}-pour-le`}
              type="date"
              value={b.pourLe}
              onChange={(e) => changer({ pourLe: e.target.value })}
              data-testid="composer-champ-pour-le"
            />
          </div>

          {refus !== null ? (
            <div className="callout callout--rare" role="alert" data-testid="composer-refus">
              <strong>Séquence refusée.</strong> {refus}
            </div>
          ) : null}

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={envoi || b.elements.length === 0}
            onClick={() => void deposer()}
            data-testid="composer-deposer"
          >
            {sequence === null
              ? `Déposer dans la liste de ${eleve.prenom}`
              : 'Enregistrer la séquence'}
          </button>
        </section>
      </div>

      {/* ── Sous 1 000 px : ce qu'on ajoute reste en vue (`UX-7-15`) ───────── */}
      <div className="composer-bandeau" data-testid="composer-bandeau" role="status">
        <span className="composer-bandeau__texte">
          <strong>
            {b.elements.length === 0
              ? 'Séquence vide'
              : `${denombrer(b.elements.length, 'élément')} dans la liste de ${eleve.prenom}`}
          </strong>
          {dernier === undefined ? null : (
            <span className="composer-bandeau__dernier">
              Dernier ajouté : <TexteMaths texte={dernier.titre} />
            </span>
          )}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => liste.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          data-testid="composer-voir-liste"
        >
          Voir la liste
        </button>
      </div>
    </>
  );
}
